import i18n from "i18next";
import { createSelector } from "reselect";
import { createStateAccessSelector } from "../../utilities/ReselectCustomMethods";
import {
  isAppInWhiteLabelMode,
  isAppInViewMode,
  isAppInShareMode,
  shareCapabilities,
  userTypes,
  wlSetupType
} from "../../utilities/Common";
import { getCustodianHistoryFormattedDateString } from "../../utilities/Number";
import { decompressObject } from "../../utilities/CompressedSerialize";
import merge from "lodash.merge";
import { toUserTimezoneDate } from "../../utilities/Timezone";

export const PVST_VALUE_TICKER_ID = 171;
export const RECAP_CATEGORY_TYPE_ASSET = "Assets";
export const RECAP_CATEGORY_TYPE_FIAT_ASSET = "Fiat Assets";
export const RECAP_CATEGORY_TYPE_ASSET_TOTAL = "AssetsTotal";
export const RECAP_CATEGORY_TYPE_DEBT_TOTAL = "DebtsTotal";
export const RECAP_CATEGORY_TYPE_NETWORTH = "Net Worth";
export const RECAP_CATEGORY_TYPE_DEBT = "Debts";
export const UNKNOWN_TICKER_SHORT_NAME = "UNKNOWN";
export const UNKNOWN_TICKER_ID = 3180;
export const HARDCODED_EXCHANGE_RATES = new Map([["EUR-BGN", 1.95583], ["BGN-EUR", 0.5113]]);
export const SIMILAR_FORMAT_CURRENCIES = new Map([
  ["INR", ["INR", "NPR"]],
  [
    "COP",
    [
      "ARS",
      "BOB",
      "CLP",
      "COP",
      "CRC",
      "CUP",
      "DOP",
      "SVC",
      "GTQ",
      "HNL",
      "MXN",
      "NIO",
      "PAB",
      "PYG",
      "PEN",
      "UYU",
      "VES"
    ]
  ]
]);
export const pickProperties = (item, propertiesToUpdate) => {
  if (!propertiesToUpdate === true || propertiesToUpdate.length === 0) {
    return item;
  }
  try {
    return propertiesToUpdate.reduce((changes, property) => {
      if (item[property] !== undefined) {
        changes[property] = item[property];
      }
      return changes;
    }, {});
  } catch {
    return item;
  }
};

export const SWISS_DISPLAY_SEPARATOR = "\u2019";
export const SWISS_RAW_SEPARATOR = "'";
export const chartContent = {
  CONTENTS: "contents",
  REPORTS: "reports",
  CONNECTION_ERROR: "connection_error",
  CONNECTIVITY_WIDGET: "connectivity_widget",
  CONTENTS_GROUPED_BY_SHEETS_AND_SECTION: "contents_grouped_by_sheets_and_sections",
  INVESTABLE_ASSETS_GROUPED_BY_SECTION: "investable_assets_grouped_by_sections",
  INVESTABLE_ASSETS_WITHOUT_CASH_GROUPED_BY_SECTION: "investable_assets_without_cash_grouped_by_sections",
  INVESTABLE_ASSETS_WITHOUT_CASH_GROUPED_BY_SHEETS_AND_SECTION:
    "investable_assets_without_cash_grouped_by_sheets_and_sections",
  ASSETS_GROUPED_BY_SECTIONS: "assets_grouped_by_sections"
};
export const chartTimeRange = {
  TODAY: "today",
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  YEARLY: "yearly",
  YTD: "ytd",
  QTD: "qtd",
  ALL: "all",
  LAST_YEAR_END: "lastYearEnd",
  ONE_YEAR_AGO: "oneYearAgo"
};
export const recapChartOptions = {
  NETWORTH: {
    id: "networth",
    label: i18n.t("supportedReport.networth.label"),
    type: "chartOption",
    apiKey: "ctr",
    fileName: i18n.t("supportedReport.networth.fileName")
  },

  SHEETS_AND_SECTIONS: {
    id: "sheets_and_sections",
    label: i18n.t("supportedReports.sheetsAndSections.label"),
    type: "chartOption",
    apiKey: "sId",
    fileName: i18n.t("supportedReports.sheetsAndSections.fileName")
  },
  ASSET_CLASSES: {
    id: "asset_classes",
    label: i18n.t("supportedReports.assetClasses.label"),
    type: "chartOption",
    apiKey: "aCls",
    fileName: i18n.t("supportedReports.assetClasses.fileName")
  },
  INVESTABLE: {
    id: "investable",
    label: i18n.t("supportedReport.investable.label"),
    type: "chartOption",
    apiKey: "aCls",
    fileName: i18n.t("supportedReport.investable.fileName")
  },
  INVESTABLE_WITHOUT_CASH: {
    id: "investable_without_cash",
    label: "Investable Assets ex Cash",
    type: "chartOption",
    apiKey: "aCls",
    fileName: i18n.t("supportedReport.investableWithoutCash.fileName")
  },
  CASH_ON_HAND: {
    id: "cash_on_hand",
    label: i18n.t("supportedReports.cashonHand.label"),
    type: "chartOption",
    apiKey: "tp",
    fileName: i18n.t("supportedReports.cashonHand.fileName")
  },

  ASSETS_AND_CURRENCY: {
    id: "assets_and_currency",
    label: i18n.t("supportedReports.assetsAndCurrency.label"),
    type: "chartOption",
    apiKey: "vTId",
    fileName: i18n.t("supportedReports.assetsAndCurrency.fileName")
  },
  STOCKS_AND_GEOGRAPHY: {
    id: "stocks_and_geography",
    label: i18n.t("supportedReports.stocksAndGeography.label"),
    type: "chartOption",
    apiKey: "cntN",
    fileName: i18n.t("supportedReports.stocksAndGeography.fileName")
  },
  STOCKS_AND_SECTOR: {
    id: "stocks_and_sector",
    label: i18n.t("supportedReports.stocksAndSector.label"),
    type: "chartOption",
    apiKey: "secN",
    fileName: i18n.t("supportedReports.stocksAndSector.fileName")
  },
  STOCKS_AND_MARKETCAP: {
    id: "stocks_and_marketcap",
    label: i18n.t("supportedReports.stocksAndMarketCap.label"),
    type: "chartOption",
    apiKey: "mCTp",
    fileName: i18n.t("supportedReports.stocksAndMarketCap.fileName")
  },
  CRYPTO: {
    id: "crypto",
    label: i18n.t("supportedReports.crypto.label"),
    type: "chartOption",
    apiKey: "secN",
    fileName: i18n.t("supportedReports.crypto.fileName")
  },
  BROKERAGES: {
    id: "brokerages",
    label: "Brokerages",
    type: "chartOption",
    apiKey: "n",
    fileName: i18n.t("supportedReports.brokerages.fileName")
  },
  TAXABLE_ASSETS: {
    id: "taxable_assets",
    label: "Assets x Taxability",
    type: "chartOption",
    apiKey: "ctr"
  }
};

export const tickerReducerInitialState = {
  tickerData: null,
  lastTickerDataUpdateTs: null,
  tickerIdMap: {},
  tickerShortNameMap: {
    UNKNOWN: {
      id: 3180,
      type: "fiat",
      code: "UNKNOWN",
      shortName: "UNKNOWN",
      name: "UNKNOWN",
      symbol: [],
      popular: -1,
      sortKey: "9",
      market: "FIAT",
      subType: null,
      currency: "USD"
    }
  },
  tickerSymbolMap: {}
};

export const categoryType = {
  ASSET: "Asset",
  DEBT: "Debt",
  INSURANCE: "Insurance",
  ALL: "All"
};

export const accountLinkingService = {
  YODLEE: 1,
  PLAID: 2,
  ZABO: 3,
  SALTEDGE: 4,
  SALTEDGE_EU: 5,
  ZILLOW: 6,
  DOMAINS: 7,
  CARS: 8,
  ZERION: 9,
  IN_HOUSE_CRYPTO_OAUTH: 10,
  IN_HOUSE_CRYPTO_API: 11,
  FLINKS_BANKING: 12,
  FLINKS_INVESTMENT: 13,
  AKAHU: 14,
  LEAN: 15,
  FINICITY: 16,
  SNAPTRADE: 17,
  MX: 18,
  IN_HOUSE_OAUTH: 20,
  KUBERA_PORTFOLIO: 19,
  MANUAL_CONNECTED: 21,
  AKOYA: 23,
  AI_APPRAISER: 24
};

export const VERIFIED_LINK_TYPES = [
  accountLinkingService.YODLEE,
  accountLinkingService.PLAID,
  accountLinkingService.IN_HOUSE_OAUTH,
  accountLinkingService.SALTEDGE,
  accountLinkingService.SALTEDGE_EU,
  accountLinkingService.IN_HOUSE_CRYPTO_OAUTH,
  accountLinkingService.FLINKS_BANKING,
  accountLinkingService.FLINKS_INVESTMENT,
  accountLinkingService.AKAHU,
  accountLinkingService.LEAN,
  accountLinkingService.FINICITY,
  accountLinkingService.SNAPTRADE,
  accountLinkingService.MX,
  accountLinkingService.AKOYA
];

// Selectors
const parseParams = params => {
  return Object.fromEntries(new URLSearchParams(params));
};

export const portfolioCurrencySelector = (state, portfolio) => {
  return portfolio ? portfolio.currency : "";
};

export const portfoliosReducerName = portfolioUserId => {
  if (!portfolioUserId === true) {
    return "portfolios";
  }
  return `portfolios-${portfolioUserId}`;
};
export const portfoliosStateSelector = state => {
  const portfolioUserId = getPortfolioSessionUserId();
  const currentUser = userSelector(state);
  return state[
    portfoliosReducerName(!currentUser === false && portfolioUserId !== currentUser.id ? portfolioUserId : null)
  ];
};
export const portfoliosSelector = state => portfoliosStateSelector(state).portfolios;
export const portfoliosMapSelector = createSelector(
  [portfoliosSelector],
  portfolios => {
    return portfolios.reduce((map, obj) => {
      map[obj.id] = obj;
      return map;
    }, {});
  }
);
export const userSelector = state => state.auth.user;
export const isSharedPortfolioUserSelector = state => {
  const user = userSelector(state);
  const sessionUserId = getPortfolioSessionUserId();
  if (isAppInWhiteLabelMode() && user && user.wl?.type === userTypes.SUB_USER) {
    return true;
  }
  if (!user === false && !sessionUserId === false && sessionUserId !== user.id) {
    return true;
  }
  return false;
};

const readStoredUserId = (storage, key) => {
  const storedUserId = storage.getItem(key);
  return storedUserId === "null" || storedUserId === "undefined" ? null : storedUserId;
};

var portfolioUserId = null;
export const setPortfolioSessionUserId = userId => {
  if (!userId === true) return;
  portfolioUserId = userId;
  sessionStorage.setItem("portfolioUserId", userId);
};

export const removePortfolioSessionUserId = () => {
  portfolioUserId = null;
  sessionStorage.removeItem("portfolioUserId");
};

var lastUsedPortfolioUserId = null;
export const setLastUsedPortfolioUserId = userId => {
  if (!userId === true) return;
  lastUsedPortfolioUserId = userId;
  localStorage.setItem("lastUsedPortfolioUserId", userId);
};

export const getLastUsedPortfolioUserId = () => {
  if (!lastUsedPortfolioUserId === true) {
    lastUsedPortfolioUserId = readStoredUserId(localStorage, "lastUsedPortfolioUserId");
  }
  return lastUsedPortfolioUserId;
};

export const removeLastUsedPortfolioUserId = () => {
  lastUsedPortfolioUserId = null;
  localStorage.removeItem("lastUsedPortfolioUserId");
};

export const getPortfolioSessionUserId = () => {
  if (!portfolioUserId === true) {
    portfolioUserId = readStoredUserId(sessionStorage, "portfolioUserId");
  }
  return portfolioUserId;
};

export const requestUserIdSelector = state => {
  if (isAppInShareMode() === true) return null;
  const user = userSelector(state);
  const sessionUserId = getPortfolioSessionUserId();
  if (!user === true) {
    return sessionUserId;
  }
  return !sessionUserId === false && sessionUserId !== user.id ? sessionUserId : user.id;
};

export const userPreferencesSelector = state => {
  return state.auth.userPreferences;
};

export const currentPortfolioCurrencySelector = state => {
  const currentPortfolio = currentPortfolioSelector(state);
  return portfolioCurrencySelector(state, currentPortfolio);
};

export const custodianSelector = (state, custodianId) => {
  if (!custodianId === true) {
    return null;
  }
  const portfoliosState = portfoliosStateSelector(state);
  var portfolios = portfoliosState.portfolios;
  if (!portfolios) {
    return null;
  }
  return findCustodianInPortfolios(portfoliosState, custodianId);
};

export const isManualConnectedHolding = (state, custodian) => {
  if (!custodian || !custodian.parentId) {
    return false;
  }
  return custodianSelector(state, custodian.parentId)?.linkType === accountLinkingService.MANUAL_CONNECTED;
};

export const linkedPortfolioCustodianInCategorySelector = (state, category, linkProviderAccountId) => {
  const currentPortfolio = currentPortfolioSelector(state);

  const custodian = currentPortfolio.details.custodian.find(
    custodian => custodian.linkProviderAccountId === linkProviderAccountId && custodian.linkContainer === category
  );
  if (custodian) {
    return custodian;
  }
  return null;
};

export const custodianSheetSelector = (state, custodianId) => {
  if (!custodianId === true) {
    return null;
  }
  const portfolios = portfoliosStateSelector(state).portfolios;
  if (portfolios === null) {
    return null;
  }

  const currentCustodian = custodianSelector(state, custodianId);
  const currentPortfolio = findPortfolioUsingCustodianIdSelector(state, custodianId);
  if (!currentPortfolio || !currentCustodian) {
    return null;
  }
  const currentSection = currentPortfolio.details.section.find(section => section.id === currentCustodian.sectionId);
  if (!currentSection === true) {
    return null;
  }
  return currentPortfolio.details.sheet.find(sheet => sheet.id === currentSection.sheetId);
};

export function getPVSTRate(state, rateStr, targetShortName) {
  if (!rateStr) return 0;
  let price = 1;

  try {
    const parsedRateDetails = JSON.parse(rateStr);
    if (parsedRateDetails?.t) {
      const rateTicker = getTickerUsingId(state, parsedRateDetails.t);
      price = getExchangeRate(state, rateTicker.shortName, targetShortName) * parsedRateDetails.p || 0;
    }
  } catch (e) {
    console.error("Error parsing rate string:", e);
  }
  return price;
}

export const isCryptoLinkingService = linkType => {
  if (!linkType === true) {
    return false;
  }

  return [
    accountLinkingService.ZABO,
    accountLinkingService.ZERION,
    accountLinkingService.IN_HOUSE_CRYPTO_API,
    accountLinkingService.IN_HOUSE_CRYPTO_OAUTH
  ].includes(linkType);
};

export const isValueEditableLinkingService = linkType => {
  if (!linkType === true) {
    return false;
  }

  return [
    accountLinkingService.ZILLOW,
    accountLinkingService.DOMAINS,
    accountLinkingService.CARS,
    accountLinkingService.AI_APPRAISER
  ].includes(linkType);
};

export const VERIFIED_CONNECTION_MILESTONES = [1, 3];

export const isVerifiedLinkingService = linkType => {
  if (!linkType === true) {
    return false;
  }

  return VERIFIED_LINK_TYPES.includes(linkType);
};

export const isAssetCustodian = (linkContainer, linkType) => {
  if (!linkContainer === true || !linkType === true) {
    return false;
  }
  const assetContainers = ["investment", "bank", "depository", "other", "insurance"];
  return (
    assetContainers.includes(linkContainer) ||
    (isCryptoLinkingService(linkType) && ["loan"].includes(linkContainer) === false)
  );
};

export const calcCustodianOwnershipValue = (value, ownership) => {
  if (value === null || value === undefined || ownership === undefined) {
    return value;
  }

  const calculatedVal = value * (ownership / 100);
  return !isNaN(calculatedVal) ? calculatedVal : value;
};

export function getCustodianValue(state, custodian, category, portfolioTicker, convertToPortfolioCurrency = true) {
  if (!custodian.name === false && !custodian.value === false) {
    let value;
    if (custodian.valueTickerId === PVST_VALUE_TICKER_ID) {
      if (custodian.rate) {
        const price = getPVSTRate(state, custodian.rate, portfolioTicker.shortName);
        value =
          category === categoryType.DEBT && isAssetCustodian(custodian.linkContainer, custodian.linkType)
            ? -custodian.value * price
            : custodian.value * price;
      } else {
        value = 0;
      }
    } else {
      value =
        category === categoryType.DEBT && isAssetCustodian(custodian.linkContainer, custodian.linkType)
          ? -custodian.value
          : custodian.value;
    }
    if (
      !custodian.valueTickerId === true ||
      custodian.valueTickerId === portfolioTicker.id ||
      custodian.valueTickerId === PVST_VALUE_TICKER_ID ||
      convertToPortfolioCurrency === false
    ) {
      return calcCustodianOwnershipValue(value, custodian.ownership);
    } else {
      const custodianTicker = getTickerUsingId(state, custodian.valueTickerId);
      const rate = getExchangeRate(state, custodianTicker.shortName, portfolioTicker.shortName);
      return calcCustodianOwnershipValue(value, custodian.ownership) * rate;
    }
  }
  return 0;
}

export const getCustodianValueById = (state, custodianId, convertToPortfolioCurrency = true) => {
  const custodian = custodianSelector(state, custodianId);
  if (!custodian) return 0;

  const custodianSheet = custodianSheetSelector(state, custodianId);
  if (!custodianSheet) return 0;

  const currentPortfolio = currentPortfolioSelector(state);
  const portfolioTicker = getTickerUsingShortName(state, currentPortfolio.currency);

  return getCustodianValue(state, custodian, custodianSheet.category, portfolioTicker, convertToPortfolioCurrency);
};

const portfolioProofsDefaultState = [];
export const portfolioProofsSelector = state => {
  const currentPortfolio = currentPortfolioSelector(state);
  if (!currentPortfolio) return portfolioProofsDefaultState;
  const portfolioState = portfoliosStateSelector(state);
  return portfolioState?.proofsByPortfolioId?.[currentPortfolio.id] || portfolioProofsDefaultState;
};

export const proofAssetsAndDebtsInCategoriesSelector = createStateAccessSelector(
  [state => state, state => currentPortfolioSelector(state)],
  (state, currentPortfolio) => {
    const sheetIdToCategoryMap = new Map();
    if (!currentPortfolio)
      return {
        assetsVerified: new Map(),
        assetsManuallyAdded: new Map(),
        debtsVerified: new Map(),
        debtsManuallyAdded: new Map(),
        assetsVerifiedTotal: 0,
        assetsManuallyAddedTotal: 0,
        debtsVerifiedTotal: 0,
        debtsManuallyAddedTotal: 0
      };
    for (const sheet of currentPortfolio.details.sheet) {
      sheetIdToCategoryMap.set(sheet.id, sheet.category);
    }

    const sectionIdToSheetIdMap = new Map();
    for (const section of currentPortfolio.details.section) {
      sectionIdToSheetIdMap.set(section.id, section.sheetId);
    }

    // Loop through currentportfolio.details. Return object with 4 Maps with keys as custodianid and value custodian object
    // 4 Maps are Assets/Debts with custodian.isVerified = true and Assets/Debts manually added
    const assetsVerified = new Map();
    let assetsVerifiedTotal = 0;
    const assetsManuallyAdded = new Map();
    let assetsManuallyAddedTotal = 0;
    const debtsVerified = new Map();
    let debtsVerifiedTotal = 0;
    const debtsManuallyAdded = new Map();
    let debtsManuallyAddedTotal = 0;

    for (const custodian of currentPortfolio.details.custodian) {
      if (
        !custodian.name ||
        [null, undefined, 0].includes(custodian.value) ||
        custodian.isLinking === true ||
        custodian.parentId
      )
        continue;
      const sheetId = sectionIdToSheetIdMap.get(custodian.sectionId);
      const category = sheetIdToCategoryMap.get(sheetId);
      if (custodian.isVerified) {
        if (category === "Asset") {
          assetsVerified.set(custodian.id, custodian);
          assetsVerifiedTotal += getCustodianValueById(state, custodian.id);
        } else if (category === "Debt") {
          debtsVerified.set(custodian.id, custodian);
          debtsVerifiedTotal += getCustodianValueById(state, custodian.id);
        }
      } else {
        if (category === "Asset") {
          assetsManuallyAdded.set(custodian.id, custodian);
          assetsManuallyAddedTotal += getCustodianValueById(state, custodian.id);
        } else if (category === "Debt") {
          debtsManuallyAdded.set(custodian.id, custodian);
          debtsManuallyAddedTotal += getCustodianValueById(state, custodian.id);
        }
      }
    }
    return {
      assetsVerified,
      assetsManuallyAdded,
      debtsVerified,
      debtsManuallyAdded,
      assetsVerifiedTotal,
      assetsManuallyAddedTotal,
      debtsVerifiedTotal,
      debtsManuallyAddedTotal
    };
  }
);

export const currentPortfolioIdSelector = state => {
  const portfolioState = portfoliosStateSelector(state);
  var url = new URL(window.location.href);
  const paramValue = url.searchParams.get("portfolio_id");
  if (paramValue) {
    const portfolios = portfolioState.portfolios;
    if (!portfolios === false && portfolios.find(item => item.id === paramValue)) {
      return paramValue;
    }
  }
  if (isAppInViewMode() === true) {
    return portfolioState.portfolios?.[0]?.id ?? null;
  }
  return portfolioState.currentPortfolioId;
};

export const currentPortfolioSelector = createSelector(
  [portfoliosSelector, currentPortfolioIdSelector],
  (portfolios, currentPortfolioId) => {
    if (portfolios === null) {
      return null;
    }
    const currentPortfolioIndex = (portfolios || []).findIndex(portfolio => {
      return portfolio.id === currentPortfolioId;
    });
    return currentPortfolioIndex === -1 ? null : portfolios[currentPortfolioIndex];
  }
);

const EMPTY_TAGS = [];

export const portfolioTagsSelector = state => currentPortfolioSelector(state)?.details?.tag ?? EMPTY_TAGS;

export const isDefaultPersonTag = tag => tag.type === "person" && tag.isDefault === 1;

export const findDefaultPersonTag = tags => (tags ?? []).find(tag => isDefaultPersonTag(tag) === true);

export const defaultPersonTagSelector = state => findDefaultPersonTag(portfolioTagsSelector(state));

export const allPortfolioPersonTagNamesSelector = createSelector(
  [portfoliosSelector],
  portfolios => {
    if (!portfolios) {
      return EMPTY_TAGS;
    }
    const names = [];
    const seen = new Set();
    for (const portfolio of portfolios) {
      for (const tag of portfolio.details?.tag ?? []) {
        const name = (tag.name ?? "").trim();
        const key = name.toLowerCase();
        if (tag.type !== "person" || isDefaultPersonTag(tag) === true || name === "" || seen.has(key)) {
          continue;
        }
        seen.add(key);
        names.push(name);
      }
    }
    return names;
  }
);

const EMPTY_TAG_ASSIGNMENTS = [];

export const liveTagAssignmentsForPortfolio = portfolio => {
  if (!portfolio === true) {
    return EMPTY_TAG_ASSIGNMENTS;
  }
  const details = portfolio.details ?? {};
  const entityIdsByType = {
    custodian: new Set((details.custodian ?? []).map(item => item.id)),
    section: new Set((details.section ?? []).map(item => item.id)),
    sheet: new Set((details.sheet ?? []).map(item => item.id))
  };
  return (details.tagAssignment ?? []).filter(assignment => {
    if (assignment.entityType === "portfolio") {
      return assignment.entityId === portfolio.id;
    }
    const entityIds = entityIdsByType[assignment.entityType];
    return !entityIds === true ? false : entityIds.has(assignment.entityId);
  });
};

export const liveTagAssignmentsSelector = state => liveTagAssignmentsForPortfolio(currentPortfolioSelector(state));

export const portfolioTagMembersSelector = state => {
  const currentPortfolio = currentPortfolioSelector(state);
  return currentPortfolio?.details?.tagMember ?? [];
};

export const ownershipEntityIdForCustodian = custodian => {
  if (!custodian === true) {
    return null;
  }
  return !custodian.parentId === true ? custodian.id : custodian.parentId;
};

export const resolveEffectiveTagAssignmentsForEntity = (portfolio, entityType, entityId) => {
  if (!portfolio === true || !entityId === true) {
    return [];
  }

  const assignments = portfolio.details?.tagAssignment ?? [];
  const assignmentsForEntity = (type, id) =>
    assignments.filter(
      assignment => assignment.entityType === type && assignment.entityId === id && (assignment.percentage ?? 0) > 0
    );

  const sections = portfolio.details?.section ?? [];
  let levels;
  if (entityType === "custodian") {
    const custodian = (portfolio.details?.custodian ?? []).find(item => item.id === entityId);
    const section = sections.find(item => item.id === custodian?.sectionId);
    levels = [
      ["custodian", ownershipEntityIdForCustodian(custodian) || entityId],
      ["section", custodian?.sectionId],
      ["sheet", section?.sheetId],
      ["portfolio", portfolio.id]
    ];
  } else if (entityType === "section") {
    const section = sections.find(item => item.id === entityId);
    levels = [["section", entityId], ["sheet", section?.sheetId], ["portfolio", portfolio.id]];
  } else if (entityType === "sheet") {
    levels = [["sheet", entityId], ["portfolio", portfolio.id]];
  } else {
    levels = [["portfolio", portfolio.id]];
  }

  for (const [type, id] of levels) {
    if (!id === true) {
      continue;
    }
    const entityAssignments = assignmentsForEntity(type, id);
    if (entityAssignments.length > 0) {
      return entityAssignments;
    }
  }
  return [];
};

export const resolveEffectiveTagAssignments = (portfolio, custodianId) =>
  resolveEffectiveTagAssignmentsForEntity(portfolio, "custodian", custodianId);

const resolveDefaultOwner = (tags, fallbackOwnerName) => {
  const defaultTag = findDefaultPersonTag(tags);
  if (!defaultTag === false) {
    return { name: defaultTag.name, tagId: defaultTag.id };
  }
  const fallbackName = (fallbackOwnerName ?? "").trim();
  return fallbackName === "" ? null : { name: fallbackName, tagId: null };
};

export const buildEffectiveOwnershipRows = (effectiveAssignments, tags, fallbackOwnerName = null) => {
  const defaultOwner = resolveDefaultOwner(tags, fallbackOwnerName);

  const rows = effectiveAssignments
    .map(assignment => {
      const tag = tags.find(item => item.id === assignment.tagId);
      if (!tag === true) {
        return null;
      }
      return { name: tag.name, percentage: assignment.percentage ?? 0, tagId: tag.id };
    })
    .filter(Boolean);

  if (!defaultOwner === true) {
    return rows;
  }

  if (rows.length === 0) {
    return [{ name: defaultOwner.name, percentage: 100, tagId: defaultOwner.tagId }];
  }

  const total = rows.reduce((sum, row) => sum + (row.percentage || 0), 0);
  if (total < 100) {
    const defaultOwnerNameKey = (defaultOwner.name ?? "").trim().toLowerCase();
    const defaultRow = rows.find(row =>
      !defaultOwner.tagId === true
        ? (row.name ?? "").trim().toLowerCase() === defaultOwnerNameKey
        : row.tagId === defaultOwner.tagId
    );
    if (defaultRow) {
      defaultRow.percentage = (defaultRow.percentage || 0) + (100 - total);
    } else {
      rows.push({ name: defaultOwner.name, percentage: 100 - total, tagId: defaultOwner.tagId });
    }
  }
  return rows;
};

export const effectiveTagAssignmentsForCustodianSelector = (state, custodianId) => {
  return resolveEffectiveTagAssignments(currentPortfolioSelector(state), custodianId);
};

export const getCustodianCost = (state, cost, costExchangeRateString, costCurrency, targetCurrency) => {
  return cost * getCustodianCostExchangeRate(state, costExchangeRateString, costCurrency, targetCurrency);
};

export const portfolioCustodiansMapSelector = createSelector(
  [portfoliosState => portfoliosState],
  portfoliosState => {
    const portfolios = portfoliosState.portfolios;
    const custodiansMap = new Map();
    const portfolioCustodianIdMap = new Map();

    for (const portfolio of portfolios) {
      for (const custodian of portfolio.details.custodian) {
        custodiansMap.set(custodian.id, custodian);
        portfolioCustodianIdMap.set(custodian.id, portfolio);
      }
    }
    return { custodiansMap, portfolioCustodianIdMap };
  }
);

export const portfolioSheetsMapSelector = createSelector(
  [portfoliosState => portfoliosState],
  portfoliosState => {
    const portfolios = portfoliosState.portfolios;
    const sheetsMap = new Map();
    const portfolioSheetIdMap = new Map();

    for (const portfolio of portfolios) {
      for (const sheet of portfolio.details.sheet) {
        sheetsMap.set(sheet.id, sheet);
        portfolioSheetIdMap.set(sheet.id, portfolio);
      }
    }
    return { sheetsMap, portfolioSheetIdMap };
  }
);

export const portfolioSectionsMapSelector = createSelector(
  [portfoliosState => portfoliosState],
  portfoliosState => {
    const portfolios = portfoliosState.portfolios;
    const sectionsMap = new Map();
    const portfolioSectionIdMap = new Map();

    for (const portfolio of portfolios) {
      for (const section of portfolio.details.section) {
        sectionsMap.set(section.id, section);
        portfolioSectionIdMap.set(section.id, portfolio);
      }
    }
    return { sectionsMap, portfolioSectionIdMap };
  }
);

export const findCustodianInPortfolios = (portfoliosState, custodianId) => {
  const portfolioCustodiansMap = portfolioCustodiansMapSelector(portfoliosState);
  if (!custodianId === true) {
    return null;
  }

  const custodian = portfolioCustodiansMap.custodiansMap.get(custodianId);
  if (custodian) {
    return custodian;
  }
  return null;
};

export const findPortfolioUsingCustodianIdSelector = (state, custodianId) => {
  const portfoliosState = portfoliosStateSelector(state);

  const portfolioCustodianIdMap = portfolioCustodiansMapSelector(portfoliosState).portfolioCustodianIdMap;
  return portfolioCustodianIdMap.get(custodianId);
};

export const hasEnabledAutopilotRules = custodian => {
  if (!custodian || !custodian.autopilotRules) {
    return false;
  }

  try {
    const rules = JSON.parse(custodian.autopilotRules);
    return rules.some(rule => rule.disabled !== 1);
  } catch (error) {
    console.error("Error parsing autopilot rules:", error);
    return false;
  }
};

export const recapReportComparisonDataSelector = createStateAccessSelector(
  [
    state => state,
    state => recapDataSelector(state),
    state => currentPortfolioCurrencySelector(state),
    (...args) => args[1],
    (...args) => args[2] ?? false,
    (...args) => args[3] ?? false,
    (...args) => args[4] ?? false,
    (...args) => args[5] ?? false
  ],
  (
    state,
    recapData,
    portfolioCurrency,
    reportId,
    shouldCompareAgainstInvestableAssets,
    shouldCompareAgainstTotalAssetsOrDebts,
    shouldCompareAgainstSheet,
    shouldCompareAgainstInvestableAssetsWithOutCash
  ) => {
    try {
      const reportParams = parseParams(reportId);
      const recapCurrency = recapDataCurrencySelector(state);
      const reportName = recapReportNameSelector(state, reportId);
      const siblings = recapReportNodeSiblingsSelector(state, reportId, chartTimeRange.TODAY);
      if (!siblings === true) {
        return null;
      }
      const nodeToFind =
        reportParams.chart_option === recapChartOptions.ASSETS_AND_CURRENCY.id &&
        reportParams.report_node_id === "Fiat Assets"
          ? checkIfAChartOptionHasNoDataToShow(recapChartOptions.CRYPTO.id, chartTimeRange.TODAY, recapData.data)
            ? RECAP_CATEGORY_TYPE_ASSET
            : RECAP_CATEGORY_TYPE_FIAT_ASSET
          : reportParams.report_node_id;
      const reportNode = siblings.find(
        item => item.name === nodeToFind || item.id === nodeToFind || item.sectionId === nodeToFind
      );
      if (!reportNode === true) {
        return null;
      }
      const chartName = getComparisonReportsTabTitleSelector(
        state,
        reportName,
        reportParams.report_node_id,
        reportParams.report_path,
        reportParams.chart_option,
        shouldCompareAgainstInvestableAssets,
        reportId,
        reportParams.chart_option === recapChartOptions.NETWORTH.id,
        shouldCompareAgainstTotalAssetsOrDebts,
        shouldCompareAgainstSheet,
        shouldCompareAgainstInvestableAssetsWithOutCash
      );
      var data = {
        name: chartName,
        value: convertCurrency(state, reportNode.values[0].value, recapCurrency, portfolioCurrency),
        currency: portfolioCurrency,
        contents: []
      };
      data.contents.push({
        name: reportNode.name,
        value: convertCurrency(state, reportNode.values[0].value, recapCurrency, portfolioCurrency),
        type: reportNode.type,
        sheetId: reportNode.sheetId,
        category: reportNode.ctr,
        sectionId: reportNode.sectionId
      });

      if (reportParams.chart_option === recapChartOptions.SHEETS_AND_SECTIONS.id) {
        data.contents[0].id = reportNode.id || reportNode.sectionId;
        var totalNode = null;
        if (shouldCompareAgainstInvestableAssets) {
          totalNode = recapDataTotalInvestableAssetsSelector(state);
          data.contents.push({
            name: i18n.t("reportCharts.label.theRemaining"),
            value: convertCurrency(
              state,
              totalNode.values[0].value - reportNode.values[0].value,
              recapCurrency,
              portfolioCurrency
            )
          });
        } else if (shouldCompareAgainstTotalAssetsOrDebts) {
          totalNode =
            reportNode.ctr === "Assets" ? recapDataTotalAssetsSelector(state) : recapDataTotalDebtsSelector(state);
          data.contents.push({
            name: i18n.t("reportCharts.label.theRemaining"),
            value: convertCurrency(
              state,
              totalNode.values[0].value - reportNode.values[0].value,
              recapCurrency,
              portfolioCurrency
            )
          });
        } else if (shouldCompareAgainstSheet) {
          totalNode = sheetAndSectionReportNodeSelector(state, reportId, null, false, true);
          data.contents.push({
            name: i18n.t("reportCharts.label.theRemaining"),
            value: convertCurrency(
              state,
              totalNode.values[0].value - reportNode.values[0].value,
              recapCurrency,
              portfolioCurrency
            )
          });
        } else {
          const everythingElseTotal = siblings.reduce((total, temp) => {
            if (
              temp.id !== reportParams.report_node_id &&
              (!temp.sectionId === true || temp.sectionId !== reportParams.report_node_id)
            ) {
              return total + temp.values[0].value;
            }
            return total;
          }, 0);
          data.contents.push({
            name: i18n.t("reportCharts.label.theRemaining"),
            value: convertCurrency(state, everythingElseTotal, recapCurrency, portfolioCurrency)
          });
        }
      } else {
        data.contents[0].id = reportNode.clubbingKey && reportNode.clubbingKey === "id" ? reportNode.id : undefined;

        if (
          (reportParams.chart_option === recapChartOptions.INVESTABLE.id ||
            reportParams.chart_option === recapChartOptions.INVESTABLE_WITHOUT_CASH.id ||
            reportParams.chart_option === recapChartOptions.CRYPTO.id ||
            reportParams.chart_option === recapChartOptions.STOCKS_AND_GEOGRAPHY.id ||
            reportParams.chart_option === recapChartOptions.STOCKS_AND_SECTOR.id ||
            reportParams.chart_option === recapChartOptions.STOCKS_AND_MARKETCAP.id ||
            reportParams.chart_option === recapChartOptions.CASH_ON_HAND.id ||
            reportParams.chart_option === recapChartOptions.BROKERAGES.id ||
            reportParams.chart_option === recapChartOptions.TAXABLE_ASSETS.id) &&
          !reportParams.report_path === true
        ) {
          if (
            reportParams.report_node_id === "Crypto" &&
            reportParams.chart_option !== recapChartOptions.INVESTABLE.id &&
            reportParams.chart_option !== recapChartOptions.INVESTABLE_WITHOUT_CASH.id
          ) {
            totalNode = recapDataTotalAssetsSelector(state);
          } else if (
            reportParams.report_node_id === "Investable Assets" ||
            reportParams.report_node_id === "Investable Assets ex Cash"
          ) {
            totalNode = recapDataTotalAssetsSelector(state);
          } else if (reportParams.report_node_id === "Cash on hand") {
            totalNode = shouldCompareAgainstInvestableAssets
              ? recapDataTotalInvestableAssetsSelector(state)
              : recapDataTotalAssetsSelector(state);
          } else {
            totalNode = siblings.find(
              item =>
                item.type === "investableAssetsTotal" ||
                item.type === "cryptoTotal" ||
                item.type === "stocksTotal" ||
                item.type === "brokeragesTotal" ||
                item.type === "taxableAssetsTotal"
            );
          }
        } else if (!reportParams.report_path === true) {
          totalNode = siblings.find(
            item => item.type === RECAP_CATEGORY_TYPE_ASSET_TOTAL || item.type === RECAP_CATEGORY_TYPE_DEBT_TOTAL
          );
        } else {
          if (shouldCompareAgainstInvestableAssets) {
            totalNode = recapDataTotalInvestableAssetsSelector(state);
          } else if (shouldCompareAgainstInvestableAssetsWithOutCash) {
            totalNode = recapDataTotalInvestableAssetsWithoutCashSelector(state);
          } else {
            totalNode = siblings.find(item => item.type === "header");
          }
        }
        if (!totalNode === true) {
          return null;
        }
        data.contents.push({
          name: i18n.t("reportCharts.label.theRemaining"),
          value: convertCurrency(
            state,
            totalNode.values[0].value - reportNode.values[0].value,
            recapCurrency,
            portfolioCurrency
          )
        });
      }

      data.total = data.contents.reduce((total, temp) => {
        return total + temp.value;
      }, 0);
      return data;
    } catch (e) {
      console.log("e", e);
      return null;
    }
  }
);

export const recapReportContentsDataSelector = createStateAccessSelector(
  [
    state => state,
    state => recapDataSelector(state),
    state => recapDataCurrencySelector(state),
    currentPortfolioCurrencySelector,
    (
      state,
      reportId,
      isInvestableAssetsBySheetsAndSectionsChart,
      isInvestableAssetsBySectionsChart,
      isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
      isInvestableAssetsWithoutCashBySectionsChart,
      isAssetsBySectionsChart
    ) =>
      recapReportNameSelector(
        state,
        reportId,
        isInvestableAssetsBySheetsAndSectionsChart,
        isInvestableAssetsBySectionsChart,
        isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
        isInvestableAssetsWithoutCashBySectionsChart,
        isAssetsBySectionsChart
      ),
    (
      state,
      reportId,
      isInvestableAssetsBySheetsAndSectionsChart,
      isInvestableAssetsBySectionsChart,
      isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
      isInvestableAssetsWithoutCashBySectionsChart,
      isAssetsBySectionsChart
    ) =>
      recapReportNodeSelector(
        state,
        reportId,
        true,
        chartTimeRange.TODAY,
        isInvestableAssetsBySheetsAndSectionsChart,
        isInvestableAssetsBySectionsChart,
        isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
        isInvestableAssetsWithoutCashBySectionsChart,
        isAssetsBySectionsChart
      ),
    state => reportPreferencesSelector(state),
    (...args) => args[1],
    (...args) => args[2] ?? false,
    (...args) => args[3] ?? false,
    (...args) => args[4] ?? false,
    (...args) => args[5] ?? false,
    (...args) => args[6] ?? false
  ],
  (
    state,
    recapData,
    recapCurrency,
    portfolioCurrency,
    reportName,
    node,
    _,
    reportId,
    isInvestableAssetsBySheetsAndSectionsChart,
    isInvestableAssetsBySectionsChart,
    isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
    isInvestableAssetsWithoutCashBySectionsChart,
    isAssetsBySectionsChart
  ) => {
    try {
      const reportParams = parseParams(reportId);
      const recapDataForToday = recapData.data[chartTimeRange.TODAY];
      const recapDataForChartOption = recapDataForToday[reportParams.chart_option];
      if (!node === true) {
        return null;
      }
      const nodeId = reportParams.report_node_id;
      let chartName;
      if (reportParams.chart_option === recapChartOptions.ASSETS_AND_CURRENCY.id && nodeId === "Fiat Assets") {
        chartName = checkIfAChartOptionHasNoDataToShow(
          recapChartOptions.CRYPTO.id,
          chartTimeRange.TODAY,
          recapData.data
        )
          ? "Assets x Currency"
          : "Fiat Assets x Currency";
      } else {
        chartName = getContentsTabTitle(
          state,
          reportParams.chart_option,
          nodeId,
          reportParams.report_path,
          reportName,
          reportId,
          false,
          isInvestableAssetsBySheetsAndSectionsChart,
          isInvestableAssetsBySectionsChart,
          isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
          isInvestableAssetsWithoutCashBySectionsChart,
          isAssetsBySectionsChart
        );
      }
      const totalValue = node.values[0] && node.values[0].value;
      if (!totalValue || totalValue < 0) {
        return null;
      }
      var data = {
        name: chartName,
        value: convertCurrency(state, totalValue, recapCurrency, portfolioCurrency),
        currency: portfolioCurrency,
        contents: []
      };
      if (reportParams.report_node_id === RECAP_CATEGORY_TYPE_NETWORTH) {
        const debtsNode =
          recapDataForChartOption.totals[RECAP_CATEGORY_TYPE_DEBT] &&
          recapDataForChartOption.totals[RECAP_CATEGORY_TYPE_DEBT].find(item => item.name === RECAP_CATEGORY_TYPE_DEBT);
        data.contents.push({
          name: node.name,
          value: convertCurrency(state, node.values[0].value, recapCurrency, portfolioCurrency)
        });
        data.contents.push({
          name: "Assets funded by debts",
          value: debtsNode ? convertCurrency(state, debtsNode.values[0].value, recapCurrency, portfolioCurrency) : 0
        });
      } else if (reportParams.chart_option === recapChartOptions.SHEETS_AND_SECTIONS.id) {
        var dataArray = [];
        if (!node.sheets === false) {
          dataArray = node.sheets;
        } else if (!node.sections === false) {
          dataArray = node.sections.length > 1 ? node.sections : node.sections[0].rows;
        } else {
          dataArray = node.rows;
        }
        if (isAssetsBySectionsChart) {
          let contentArray = [];
          const filteredSheets = dataArray.filter(item => item.values[0].value !== 0);
          for (const sheet of filteredSheets) {
            const sectionsInSheet = sheet.sections
              .filter(section => section)
              .map(section => {
                const isSameSectionNameAlreadyPresent =
                  filteredSheets.filter(filteredSheet =>
                    filteredSheet.sections.some(data => data.name === section.name)
                  ).length > 1;
                return {
                  name:
                    sheet.sections.length > 1
                      ? isSameSectionNameAlreadyPresent
                        ? `${sheet.name} / ${section.name}`
                        : `${section.name}`
                      : sheet.name,
                  id: section.sectionId,
                  value: convertCurrency(state, section.values[0].value, recapCurrency, portfolioCurrency)
                };
              });
            contentArray = [...contentArray, ...sectionsInSheet];
          }
          data.contents = contentArray;
        } else {
          data.contents = dataArray
            .filter(item => item && item.values[0].value !== 0)
            .map(item => {
              return {
                name: item.name,
                id: item.id || item.sectionId,
                value: convertCurrency(state, item.values[0].value, recapCurrency, portfolioCurrency),
                type: item.type,
                sheetId: item.sheetId,
                category: item.ctr,
                sectionId: item.sectionId
              };
            });
        }
      } else if (
        isInvestableAssetsBySheetsAndSectionsChart ||
        isInvestableAssetsBySectionsChart ||
        isInvestableAssetsWithoutCashBySheetsAndSectionsChart ||
        isInvestableAssetsWithoutCashBySectionsChart
      ) {
        var chartDataArray = [];
        if (!node.sheets === false) {
          chartDataArray = node.sheets;
        } else if (!node.sections === false) {
          chartDataArray = node.sections.length > 1 ? node.sections : node.sections[0].rows;
        } else {
          chartDataArray = node.rows;
        }
        data.value =
          isInvestableAssetsBySheetsAndSectionsChart || isInvestableAssetsBySectionsChart
            ? recapDataTotalInvestableAssetsValueSelector(state)
            : recapDataTotalInvestableAssetsWithoutCashValueSelector(state);
        if (isInvestableAssetsBySheetsAndSectionsChart || isInvestableAssetsWithoutCashBySheetsAndSectionsChart) {
          data.contents = chartDataArray
            .filter(item => item && item.values[0].value !== 0)
            .map(item => {
              return {
                name: item.name,
                id: item.id || item.sectionId,
                value: convertCurrency(state, item.values[0].value, recapCurrency, portfolioCurrency)
              };
            });
        } else {
          let contentArray = [];
          const filteredSheets = chartDataArray.filter(item => item && item.values[0].value !== 0);
          for (const sheet of filteredSheets) {
            const sectionsInSheet = sheet.sections
              .filter(section => section)
              .map(section => {
                const isSameSectionNameAlreadyPresent =
                  filteredSheets.filter(filteredSheet =>
                    filteredSheet.sections.some(data => data?.name === section?.name)
                  ).length > 1;
                return {
                  name:
                    sheet.sections.length > 1
                      ? isSameSectionNameAlreadyPresent
                        ? `${sheet.name} / ${section.name}`
                        : `${section.name}`
                      : sheet.name,
                  id: section.sectionId,
                  value: convertCurrency(state, section.values[0].value, recapCurrency, portfolioCurrency)
                };
              });
            contentArray = [...contentArray, ...sectionsInSheet];
          }
          data.contents = contentArray;
        }
      } else {
        if (node.type === "header") {
          const nodeid =
            (reportParams.chart_option === recapChartOptions.ASSET_CLASSES.id ||
              reportParams.chart_option === recapChartOptions.INVESTABLE.id ||
              reportParams.chart_option === recapChartOptions.INVESTABLE_WITHOUT_CASH.id) &&
            (reportParams.report_node_id === "Non-US Funds" || reportParams.report_node_id === "Miscellaneous")
              ? reportParams.report_node_id === "Non-US Funds"
                ? "Funds"
                : "Others"
              : reportParams.report_node_id;
          data.contents = recapDataForChartOption.totals[nodeid]
            .filter(item => item.name !== reportParams.report_node_id && item.values[0].value !== 0)
            .map(item => {
              return {
                name: item.name,
                id: item.clubbingKey && item.clubbingKey === "id" ? item.id : undefined,
                value: convertCurrency(state, item.values[0].value, recapCurrency, portfolioCurrency)
              };
            });
        } else {
          for (const key in recapDataForChartOption.totals) {
            if (
              reportParams.chart_option === recapChartOptions.STOCKS_AND_GEOGRAPHY.id
                ? key !== "Stocks" && key !== RECAP_CATEGORY_TYPE_NETWORTH
                : key !== reportParams.report_node_id && key !== RECAP_CATEGORY_TYPE_NETWORTH
            ) {
              const dataNode = recapDataForChartOption.totals[key].find(
                item =>
                  (item.name === "Non-US Funds" || item.name === "Miscellaneous" || item.name === key) &&
                  item.values[0].value !== 0
              );
              if (dataNode) {
                data.contents.push({
                  name: dataNode.name,
                  value: convertCurrency(state, dataNode.values[0].value, recapCurrency, portfolioCurrency),
                  type: dataNode.type
                });
              }
            }
          }
        }
      }

      data.total =
        reportParams.report_node_id === RECAP_CATEGORY_TYPE_NETWORTH
          ? data.contents.reduce((total, temp) => total + temp.value, 0)
          : data.value;

      data.contents.sort((a, b) => b.value - a.value);
      return data;
    } catch (e) {
      return null;
    }
  }
);

export const reportTargetPercentageSelector = createStateAccessSelector(
  [
    state => state,
    state => {
      const currentPortfolio = currentPortfolioSelector(state);
      return reportPreferencesSelector(state, currentPortfolio);
    },
    (...args) => args[1],
    (...args) => args[2],
    (...args) => args[3]
  ],
  (state, reportPreferences, reportId, tabName, contentId) => {
    const reportParams = parseParams(reportId);
    const chartOption = reportParams.chart_option;
    const reportNodeId = reportParams.report_node_id;
    const tabNameInPreference =
      tabName === chartContent.CONTENTS_GROUPED_BY_SHEETS_AND_SECTION ||
      tabName === chartContent.INVESTABLE_ASSETS_GROUPED_BY_SECTION ||
      tabName === chartContent.INVESTABLE_ASSETS_WITHOUT_CASH_GROUPED_BY_SECTION ||
      tabName === chartContent.INVESTABLE_ASSETS_WITHOUT_CASH_GROUPED_BY_SHEETS_AND_SECTION ||
      tabName === chartContent.ASSETS_GROUPED_BY_SECTIONS
        ? "contents"
        : tabName;

    const reportPreferencesForSelectedReport = reportPreferences.find(
      preference => preference.reportType === chartOption
    );
    const reportPreferencesDataForSelectedReport =
      reportPreferencesForSelectedReport && reportPreferencesForSelectedReport.data
        ? reportPreferencesForSelectedReport.data
        : {};
    if (
      !reportPreferencesDataForSelectedReport[reportNodeId] === true ||
      !reportPreferencesDataForSelectedReport[reportNodeId][tabNameInPreference] === true ||
      !reportPreferencesDataForSelectedReport[reportNodeId][tabNameInPreference][contentId] === true
    ) {
      return null;
    }

    return parseNumberStringToFloat(
      state,
      reportPreferencesDataForSelectedReport[reportNodeId][tabNameInPreference][contentId].target
    );
  }
);

const categoryTotalsSheetsMapSelector = createSelector(
  [(...args) => args[0]],
  categoryTotalsSheets => {
    const categoryTotalsSheetsMap = new Map();
    for (const sheet of categoryTotalsSheets) {
      if (sheet !== null) {
        categoryTotalsSheetsMap.set(sheet.id, sheet);
      }
    }
    return categoryTotalsSheetsMap;
  }
);

const categoryTotalsSectionsMapSelector = createSelector(
  [(...args) => args[0]],
  categoryTotalsSections => {
    const categoryTotalsSectionsMap = new Map();
    for (const section of categoryTotalsSections) {
      if (section !== null) {
        categoryTotalsSectionsMap.set(section.sectionId, section);
      }
    }
    return categoryTotalsSectionsMap;
  }
);

export const sheetAndSectionReportNodeSelector = (
  state,
  reportId,
  overrideTimeRange = null,
  shouldReturnParentNode = false,
  shouldReturnSheetNode = false,
  isInvestableAssetsBySheetsAndSectionsChart,
  isInvestableAssetsBySectionsChart,
  isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
  isInvestableAssetsWithoutCashBySectionsChart,
  isAssetsBySectionsChart
) => {
  const recapData = recapDataSelector(state);
  const reportParams = parseParams(reportId);
  if (
    reportParams.chart_option !== recapChartOptions.SHEETS_AND_SECTIONS.id &&
    !isInvestableAssetsBySheetsAndSectionsChart &&
    !isInvestableAssetsBySectionsChart &&
    !isInvestableAssetsWithoutCashBySheetsAndSectionsChart &&
    !isInvestableAssetsWithoutCashBySectionsChart
  ) {
    return null;
  }

  const reportPath = reportParams.report_path;
  const timeRange = overrideTimeRange || reportParams.chart_timerange;
  const reportTimeRangeNode = recapData.data[timeRange];
  let reportTotals;

  if (isInvestableAssetsBySheetsAndSectionsChart || isInvestableAssetsBySectionsChart) {
    reportTotals =
      reportTimeRangeNode &&
      reportTimeRangeNode.investable_by_sheets_and_sections &&
      reportTimeRangeNode.investable_by_sheets_and_sections.totals;
  } else if (isInvestableAssetsWithoutCashBySheetsAndSectionsChart || isInvestableAssetsWithoutCashBySectionsChart) {
    reportTotals =
      reportTimeRangeNode &&
      reportTimeRangeNode.investable_without_cash_by_sheets_and_sections &&
      reportTimeRangeNode.investable_without_cash_by_sheets_and_sections.totals;
  } else {
    reportTotals =
      reportTimeRangeNode &&
      reportTimeRangeNode[reportParams.chart_option] &&
      reportTimeRangeNode[reportParams.chart_option].totals;
  }
  if (!reportTotals) {
    return null;
  }
  if (!reportPath === true) {
    return isInvestableAssetsBySheetsAndSectionsChart ||
      isInvestableAssetsBySectionsChart ||
      isInvestableAssetsWithoutCashBySheetsAndSectionsChart ||
      isInvestableAssetsWithoutCashBySectionsChart
      ? reportTotals.Assets
      : reportTotals[reportParams.report_node_id];
  }

  const pathComponents = reportPath.split("/");
  const category = pathComponents[0];
  const type = pathComponents[1];
  const categoryTotals = reportTotals[category];
  if (type === "sheets") {
    return categoryTotalsSheetsMapSelector(categoryTotals.sheets).get(reportParams.report_node_id);
  } else if (type === "sections") {
    for (const sheet of categoryTotals.sheets) {
      if (sheet === null) continue;
      const section = categoryTotalsSectionsMapSelector(sheet.sections).get(reportParams.report_node_id);
      if (section) {
        return shouldReturnParentNode ? sheet : section;
      }
    }
  } else if (type === "rows") {
    const { sheets = [] } = categoryTotals || {};
    for (const sheet of sheets) {
      if (sheet === null) continue;
      for (const section of sheet.sections) {
        // @TODO: Too many null values in rows. Need to fix this
        const rowIndex = section.rows
          ? section.rows.findIndex(item => item && item.id === reportParams.report_node_id)
          : -1;
        if (rowIndex !== -1) {
          if (shouldReturnParentNode) {
            return sheet.sections.length > 1 ? section : sheet;
          } else {
            return shouldReturnSheetNode ? sheet : section.rows[rowIndex];
          }
        }
      }
    }
  }
  return null;
};

const materializedRecapEntryCache = new WeakMap();
const decompressedRecapTimeRangeCache = new WeakMap();

const decompressRecapTimeRangeData = compressedData => {
  if (!decompressedRecapTimeRangeCache.has(compressedData)) {
    // Runs during render: a corrupt blob must degrade to a missing time range,
    // not crash the tree. The null failure marker is cached too so a bad blob
    // is only attempted once.
    let decompressed = null;
    try {
      decompressed = decompressObject(compressedData);
    } catch (error) {
      console.error("Error decompressing recap data:", error);
    }
    decompressedRecapTimeRangeCache.set(compressedData, decompressed);
  }
  return decompressedRecapTimeRangeCache.get(compressedData);
};

// Hydrated entries hold each time range's payload still compressed (Uint8Array);
// live-computed entries hold plain report nodes. Both caches are keyed by object
// identity so repeated calls return a stable reference (memo/selector consumers
// depend on that).
const materializeRecapEntry = entry => {
  let materialized = materializedRecapEntryCache.get(entry);
  if (materialized) {
    return materialized;
  }

  const data = {};
  for (const value of Object.values(entry.data || {})) {
    if (value instanceof Uint8Array) {
      // Every record also carries lastYearEnd, investableAssetsList,
      // taxOnUnrealisedGainsList and unfundedCustodiansList, so those four keys
      // collide across records; earlier values win, preserving the old accumulate
      // order. The fresh merge target keeps that merge from writing into the
      // cached decompressed objects.
      const decompressed = decompressRecapTimeRangeData(value);
      for (const [key, reportNode] of Object.entries(decompressed || {})) {
        data[key] = key in data ? merge({}, reportNode, data[key]) : reportNode;
      }
    }
  }
  for (const [timeRange, value] of Object.entries(entry.data || {})) {
    if (!(value instanceof Uint8Array)) {
      data[timeRange] = value;
    }
  }

  materialized = { ...entry, data };
  materializedRecapEntryCache.set(entry, materialized);
  return materialized;
};

export const recapDataForPortfolioSelector = (state, portfolioId) => {
  const recapDataPortfolioMap = portfoliosStateSelector(state).recapDataPortfolioMap;
  if (portfolioId && recapDataPortfolioMap && recapDataPortfolioMap[portfolioId]) {
    return materializeRecapEntry(recapDataPortfolioMap[portfolioId]);
  }
  return 0;
};

export const recapDataSelector = state => {
  const portfolio = currentPortfolioSelector(state);
  return recapDataForPortfolioSelector(state, portfolio && portfolio.id);
};

export const recapRawDataSelector = state => {
  if (portfoliosStateSelector(state)) {
    return portfoliosStateSelector(state).recapRawData;
  }
  return null;
};

export const vanityStatDataSelector = state => {
  const portfolio = currentPortfolioSelector(state);
  if (isAppInViewMode() || isAppInWhiteLabelMode() || portfolio?.write === 0) {
    return null;
  }
  if (portfolio && portfoliosStateSelector(state).vanityStatPortfolioMap[portfolio.id]) {
    return portfoliosStateSelector(state).vanityStatPortfolioMap[portfolio.id];
  }
  return null;
};

export const lastViewedNetworthVanitySelector = state => {
  const portfolio = currentPortfolioSelector(state);
  if (portfolio && portfoliosStateSelector(state).lastViewedNetworthVanityPortfolioMap[portfolio.id]) {
    return portfoliosStateSelector(state).lastViewedNetworthVanityPortfolioMap[portfolio.id];
  }
  return null;
};

export const primaryResidenceValueStateSelector = state => {
  return vanityStatDataSelector(state)?.primaryResidenceValue;
};

export const percentileNetworthStatSelector = state => {
  return vanityStatDataSelector(state)?.clubPercentile;
};

export const initialPercentileNetworthStatSelector = state => {
  return vanityStatDataSelector(state)?.otherClubPercentile?.initial;
};

export const vanityClubs = state => {
  return isAppInWhiteLabelMode() ? false : vanityStatDataSelector(state)?.clubs;
};

export const medianCashRatioSelector = state => {
  return isAppInWhiteLabelMode() ? null : vanityStatDataSelector(state)?.clubMedians?.cashRatio;
};

export const medianInvestableAssetsRatioSelector = state => {
  return isAppInWhiteLabelMode() ? null : vanityStatDataSelector(state)?.clubMedians?.investableRatio;
};

export const medianPrimaryResidenceValueSelector = state => {
  return isAppInWhiteLabelMode() ? null : vanityStatDataSelector(state)?.clubMedians?.primaryResidenceRatio;
};

export const isRecapDataAvailableSelector = createSelector(
  [recapDataSelector],
  recapData => {
    return !!recapData && Object.keys(recapData.data || {}).length > 0;
  }
);

export const getCustodianCostExchangeRate = (state, costExchangeRateString, costCurrency, targetCurrency) => {
  if (targetCurrency === costCurrency) {
    return 1;
  }

  var exchangeRateDetails = null;
  try {
    exchangeRateDetails = JSON.parse(costExchangeRateString);
  } catch (e) {}
  if (!exchangeRateDetails === true) {
    return getExchangeRate(state, costCurrency, targetCurrency, false);
  }

  const targetCurrencyTicker = getTickerUsingShortName(state, targetCurrency);
  if (targetCurrencyTicker.id === exchangeRateDetails.tickerId) {
    return exchangeRateDetails.rate;
  } else {
    const detailsTargetTicker = getTickerUsingId(state, exchangeRateDetails.tickerId);

    // This is to fix a bug where for tickers post searching we were using the conversion
    // rate to USD instead of mapping that to the tickers currency rate
    if (detailsTargetTicker.shortName === costCurrency && exchangeRateDetails.rate !== 1) {
      return (1 / exchangeRateDetails.rate) * getExchangeRate(state, "USD", targetCurrency);
    }

    return exchangeRateDetails.rate * getExchangeRate(state, detailsTargetTicker.shortName, targetCurrency, false);
  }
};

export const recapDataCurrencySelector = state => {
  const recapData = recapDataSelector(state);
  return !recapData === true ? null : recapData.currency;
};

export const recapReportNameSelector = (
  state,
  reportId,
  isInvestableAssetsBySheetsAndSectionsChart,
  isInvestableAssetsBySectionsChart,
  isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
  isInvestableAssetsWithoutCashBySectionsChart,
  isAssetsBySectionsChart
) => {
  if (!reportId === true) {
    return null;
  }

  const recapNode = recapReportNodeSelector(
    state,
    reportId,
    true,
    chartTimeRange.TODAY,
    isInvestableAssetsBySheetsAndSectionsChart,
    isInvestableAssetsBySectionsChart,
    isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
    isInvestableAssetsWithoutCashBySectionsChart,
    isAssetsBySectionsChart
  );
  return (recapNode && recapNode.name) || null;
};

export const recapReportTabNameSelector = (state, reportId) => {
  const recapNode = recapReportNodeSelector(state, reportId, true, chartTimeRange.TODAY);
  if (!recapNode) {
    return null;
  }

  const custodian = custodianSelector(state, recapNode.cId);
  const tickerSymbol = custodian && getTickerSymbolToPrefix(getTickerUsingId(state, custodian.valueTickerId));
  return tickerSymbol && recapNode.name?.startsWith(`${tickerSymbol} • `) ? tickerSymbol : recapNode.name || null;
};

export const recapReportNodeSiblingsSelector = (state, reportId, overrideTimeRange = null) => {
  if (!reportId === true) {
    return null;
  }

  const recapData = recapDataSelector(state);
  const reportParams = parseParams(reportId);
  const reportPath = reportParams.report_path;
  const timeRange = overrideTimeRange || reportParams.chart_timerange;
  const reportTimeRangeNode = recapData?.data?.[timeRange];
  const reportTotals = reportTimeRangeNode?.[reportParams.chart_option]?.totals;

  if (reportParams.chart_option === recapChartOptions.SHEETS_AND_SECTIONS.id) {
    const pathComponents = reportPath.split("/");
    const category = pathComponents[0];
    const type = pathComponents[1];
    const categoryTotals = reportTotals[category];

    if (type === "sheets") {
      return categoryTotals.sheets.filter(sheet => sheet !== null);
    } else if (type === "sections") {
      for (const sheet of categoryTotals.sheets) {
        const sectionIndex = sheet.sections.findIndex(item => item && item.sectionId === reportParams.report_node_id);
        if (sectionIndex !== -1) {
          return sheet.sections.filter(section => section !== null);
        }
      }
    } else if (type === "rows") {
      for (const sheet of categoryTotals.sheets) {
        for (const section of sheet.sections) {
          const rowIndex = section.rows.findIndex(item => item && item.id === reportParams.report_node_id);
          if (rowIndex !== -1) {
            return section.rows.filter(row => row !== null);
          }
        }
      }
    }
    return null;
  }

  if (!reportPath === true) {
    var siblings = [];
    for (const key in reportTotals) {
      let keyToCompare;
      if (reportParams.chart_option === recapChartOptions.ASSETS_AND_CURRENCY.id && key === "Fiat Assets") {
        keyToCompare = checkIfAChartOptionHasNoDataToShow(
          recapChartOptions.CRYPTO.id,
          chartTimeRange.TODAY,
          recapData.data
        )
          ? RECAP_CATEGORY_TYPE_ASSET
          : RECAP_CATEGORY_TYPE_FIAT_ASSET;
      } else if (
        reportParams.chart_option === recapChartOptions.ASSET_CLASSES.id ||
        reportParams.chart_option === recapChartOptions.INVESTABLE.id ||
        reportParams.chart_option === recapChartOptions.INVESTABLE_WITHOUT_CASH.id
      ) {
        keyToCompare = key === "Funds" ? "Non-US Funds" : key === "Others" ? "Miscellaneous" : key;
      } else {
        keyToCompare = key;
      }
      const node = reportTotals[key].find(item => item.name === keyToCompare);
      if (node) {
        siblings.push(node);
      }
    }
    return siblings;
  } else {
    return reportTotals[decodeURIComponent(reportPath)];
  }
};

export const checkIfTodayRecapDataIsAvailable = recapData => {
  return !!recapData?.[chartTimeRange.TODAY]?.[recapChartOptions.NETWORTH.id]?.totals;
};

const getCurrentBucketStartForTimeRange = timeRange => {
  const bucketStart = toUserTimezoneDate();
  bucketStart.setHours(0, 0, 0, 0);

  switch (timeRange) {
    case chartTimeRange.DAILY:
      return bucketStart;
    case chartTimeRange.WEEKLY:
      bucketStart.setDate(bucketStart.getDate() - bucketStart.getDay());
      return bucketStart;
    case chartTimeRange.MONTHLY:
      bucketStart.setDate(1);
      return bucketStart;
    case chartTimeRange.QUARTERLY:
      bucketStart.setMonth(bucketStart.getMonth() - (bucketStart.getMonth() % 3), 1);
      return bucketStart;
    case chartTimeRange.YEARLY:
      bucketStart.setMonth(0, 1);
      return bucketStart;
    default:
      return null;
  }
};

export const checkIfTimeRangeHasNoDataForStartDate = (timeRange, startDate) => {
  const startDateTime = new Date(startDate).getTime();
  if (isNaN(startDateTime)) {
    return false;
  }

  const bucketStart = getCurrentBucketStartForTimeRange(timeRange);
  if (!bucketStart) {
    return false;
  }

  return startDateTime >= bucketStart.getTime();
};

export const checkIfAChartOptionHasNoDataToShow = (chartOption, timeRange, recapData) => {
  try {
    if (chartOption === recapChartOptions.ASSET_CLASSES.id) {
      const assetsRow = recapData?.[timeRange]?.[chartOption]?.totals?.Assets;
      const isValuePresent = assetsRow && assetsRow[0].values.some(data => data.value > 0);
      return !isValuePresent;
    } else if (chartOption === recapChartOptions.STOCKS_AND_GEOGRAPHY.id) {
      const stocksRow = recapData?.[timeRange]?.[chartOption]?.totals?.Stocks;
      const isValuePresent = stocksRow && stocksRow[0].values.some(data => data.value > 0);
      return (
        !isValuePresent ||
        Object.values(recapData[timeRange][chartOption].totals).filter(sectionValue => sectionValue.length).length < 4
      );
    } else if (
      chartOption === recapChartOptions.STOCKS_AND_MARKETCAP.id ||
      chartOption === recapChartOptions.STOCKS_AND_SECTOR.id
    ) {
      const stocksRow = recapData?.[timeRange]?.[chartOption]?.totals?.Stocks;
      const isValuePresent = stocksRow && stocksRow[0].values.some(data => data.value > 0);
      return !isValuePresent;
    } else if (chartOption === recapChartOptions.CRYPTO.id) {
      const cryptoRow =
        recapData &&
        recapData[timeRange] &&
        recapData[timeRange][chartOption] &&
        recapData[timeRange][chartOption].totals &&
        recapData[timeRange][chartOption].totals.Crypto;
      const isValuePresent = cryptoRow && cryptoRow[0].values.some(data => data.value > 0);
      return !isValuePresent;
    } else if (chartOption === recapChartOptions.INVESTABLE.id) {
      const investableRow =
        recapData[timeRange]?.[chartOption]?.totals && recapData[timeRange][chartOption].totals["Investable Assets"];
      const isValuePresent = investableRow && investableRow[0].values.some(data => data.value > 0);
      return !isValuePresent;
    } else if (chartOption === recapChartOptions.INVESTABLE_WITHOUT_CASH.id) {
      const investableWithoutCashRow = recapData?.[timeRange]?.[chartOption]?.totals["Investable Assets ex Cash"];
      const isValuePresent =
        investableWithoutCashRow && investableWithoutCashRow[0].values.some(data => data.value > 0);
      return !isValuePresent;
    } else if (chartOption === recapChartOptions.ASSETS_AND_CURRENCY.id) {
      const assetsRow = recapData?.[timeRange]?.[chartOption]?.totals?.["Fiat Assets"];
      const isValuePresent = assetsRow && assetsRow[0].values.some(data => data.value > 0);
      return (
        !isValuePresent ||
        (Object.keys(recapData[timeRange][chartOption].totals).filter(section => section !== "undefined").length < 4 ||
          Object.values(recapData[timeRange][chartOption].totals).filter(sectionValue => sectionValue.length).length <
            4)
      );
    } else if (chartOption === recapChartOptions.BROKERAGES.id) {
      const brokeragesRow =
        recapData[timeRange][chartOption].totals && recapData[timeRange][chartOption].totals.Brokerages;
      const isValuePresent = brokeragesRow && brokeragesRow[0].values.some(data => data.value > 0);
      return !isValuePresent;
    } else {
      return Object.keys(recapData[timeRange][chartOption].totals).length <= 1;
    }
  } catch (e) {
    console.log("e", e);
  }
};

export const getComparisonReportsTabTitleSelector = (
  state,
  reportName,
  nodeId,
  reportPath,
  selectedChartOptions,
  shouldCompareAgainstInvestableAssets,
  reportId,
  shouldReturnLabel,
  shouldCompareAgainstTotalAssetsOrDebts,
  shouldCompareAgainstSheet,
  shouldCompareAgainstInvestableAssetsWithoutCash
) => {
  if (!reportPath) {
    switch (selectedChartOptions) {
      case recapChartOptions.ASSET_CLASSES.id:
      case recapChartOptions.ASSETS_AND_CURRENCY.id:
        return `${reportName} to Total Assets`;
      case recapChartOptions.STOCKS_AND_MARKETCAP.id:
      case recapChartOptions.STOCKS_AND_SECTOR.id:
        return `${reportName} to Total Stocks`;
      case recapChartOptions.STOCKS_AND_GEOGRAPHY.id:
        return `${reportName} to Total Stocks`;

      case recapChartOptions.CRYPTO.id:
        if (nodeId === "Crypto") {
          return "Crypto to Total Assets";
        }
        return `${reportName} to Total Crypto`;
      case recapChartOptions.INVESTABLE.id:
        if (nodeId === "Investable Assets") {
          return "Investables Assets to Total Assets";
        }
        return `${reportName} to Total Investable`;
      case recapChartOptions.CASH_ON_HAND.id:
        if (shouldCompareAgainstInvestableAssets) {
          return "Cash on hand to Investable Assets";
        }
        return "Cash on hand to Total Assets";
      case recapChartOptions.BROKERAGES.id:
        return `${reportName} to Total Brokerages`;
      case recapChartOptions.INVESTABLE_WITHOUT_CASH.id:
        if (nodeId === "Investable Assets ex Cash") {
          return "Investables Assets ex Cash to Total Assets";
        }
        return `${reportName} to Total Investable ex Cash`;
      case recapChartOptions.TAXABLE_ASSETS.id:
        return `${reportName} to Total Assets`;
    }
  } else {
    switch (selectedChartOptions) {
      case recapChartOptions.NETWORTH.id:
        if (reportPath === "Assets") {
          return `${!shouldReturnLabel ? `${reportName} (Sheet) to Total Assets` : `${reportName} To Total Assets`}`;
        } else if (reportPath === "Debts") {
          return `${!shouldReturnLabel ? `${reportName} (Sheet) to Total Debts` : `${reportName}To Total Debts`}`;
        }
        break;
      case recapChartOptions.SHEETS_AND_SECTIONS.id:
        if (reportPath === "Assets" || reportPath === "Assets/sheets") {
          if (shouldCompareAgainstInvestableAssets) {
            return `${!shouldReturnLabel ? `${reportName} (Sheet) to Investable Assets` : "To Investable Assets"}`;
          }
          return `${!shouldReturnLabel ? `${reportName} (Sheet) to Total Assets` : "To Total Assets"}`;
        } else if (reportPath === "Debts" || reportPath === "Debts/sheets") {
          return `${!shouldReturnLabel ? `${reportName} (Sheet) to Total Debts` : "To Total Debts"}`;
        } else if (reportPath === "Assets/sections" || reportPath === "Debts/sections") {
          const parentNode = sheetAndSectionReportNodeSelector(state, reportId, chartTimeRange.TODAY, true);
          if (shouldCompareAgainstInvestableAssets) {
            return `${
              !shouldReturnLabel ? `${parentNode.name} / ${reportName} to Investable Assets` : "To Investable Assets"
            }`;
          }
          if (shouldCompareAgainstTotalAssetsOrDebts) {
            return reportPath === "Assets/sections"
              ? `${!shouldReturnLabel ? `${parentNode.name} / ${reportName} to Total Assets` : "To Total Assets"}`
              : `${!shouldReturnLabel ? `${parentNode.name} / ${reportName} to Total Debts` : "To Total Debts"} `;
          }
          return `${
            !shouldReturnLabel ? `${reportName} (Section) to ${parentNode.name} (Sheet)` : `To ${parentNode.name}`
          }`;
        } else if (reportPath === "Assets/rows") {
          if (shouldCompareAgainstInvestableAssets) {
            return `${!shouldReturnLabel ? `${reportName} to Investable Assets` : "To Investable Assets"}`;
          }
          if (shouldCompareAgainstTotalAssetsOrDebts) {
            return `${!shouldReturnLabel ? `${reportName} to Total Assets` : "To Total Assets"}`;
          }
          const sheetNode = sheetAndSectionReportNodeSelector(state, reportId, chartTimeRange.TODAY, false, true);
          if (shouldCompareAgainstSheet) {
            return `${!shouldReturnLabel ? `${reportName} to ${sheetNode.name} (Sheet)` : `To ${sheetNode.name}`}`;
          }
          const parentNode = sheetAndSectionReportNodeSelector(state, reportId, chartTimeRange.TODAY, true);
          return `${
            !shouldReturnLabel ? `${reportName} to ${sheetNode.name} / ${parentNode.name}` : `To ${parentNode.name}`
          }`;
        } else if (reportPath === "Debts/rows") {
          if (shouldCompareAgainstTotalAssetsOrDebts) {
            return `${!shouldReturnLabel ? `${reportName} to Total Debts` : "To Total Debts"}`;
          }
          const sheetNode = sheetAndSectionReportNodeSelector(state, reportId, chartTimeRange.TODAY, false, true);
          if (shouldCompareAgainstSheet) {
            return `${!shouldReturnLabel ? `${reportName} to ${sheetNode.name} (Sheet)` : `To ${sheetNode.name}`}`;
          }
          const parentNode = sheetAndSectionReportNodeSelector(state, reportId, chartTimeRange.TODAY, true);
          return `${
            !shouldReturnLabel ? `${reportName} to ${sheetNode.name} / ${parentNode.name}` : `To ${parentNode.name}`
          }`;
        }
        break;
      case recapChartOptions.ASSET_CLASSES.id:
      case recapChartOptions.INVESTABLE.id:
      case recapChartOptions.INVESTABLE_WITHOUT_CASH.id:
        if (reportPath === "Funds") {
          return `${reportName} to Non-US Funds`;
        } else if (reportPath === "Others") {
          return `${reportName} to Miscellaneous`;
        } else if (shouldCompareAgainstInvestableAssets) {
          return `${reportName} to Total Investable`;
        }
        if (shouldCompareAgainstInvestableAssetsWithoutCash) {
          return `${reportName} to Total Investable ex Cash`;
        } else {
          return `${reportName} to ${reportPath}`;
        }
      default:
        return `${reportName} to ${reportPath}`;
    }
  }
};

export const convertCurrency = (state, value, fromCurrency, toCurrency, date) => {
  if (value === undefined) {
    return 0;
  }
  if (fromCurrency === toCurrency) {
    return value;
  }
  return getExchangeRate(state, fromCurrency, toCurrency, undefined, date) * value;
};

export const recapDataTotalInvestableAssetsSelector = state => {
  const recapData = recapDataSelector(state);
  return recapData?.data?.[chartTimeRange.TODAY]?.[recapChartOptions.INVESTABLE.id]?.totals?.["Investable Assets"]?.[0];
};

export const recapDataTotalAssetsSelector = state => {
  const recapData = recapDataSelector(state);
  return recapData?.data?.[chartTimeRange.TODAY]?.[recapChartOptions.SHEETS_AND_SECTIONS.id]?.totals?.Assets;
};

export const recapDataTotalDebtsSelector = state => {
  const recapData = recapDataSelector(state);
  return recapData?.data?.[chartTimeRange.TODAY]?.[recapChartOptions.SHEETS_AND_SECTIONS.id]?.totals?.Debts;
};

export const recapDataTotalInvestableAssetsWithoutCashSelector = state => {
  const recapData = recapDataSelector(state);
  return recapData?.data?.[chartTimeRange.TODAY]?.[recapChartOptions.INVESTABLE_WITHOUT_CASH.id]?.totals?.[
    "Investable Assets ex Cash"
  ]?.[0];
};

export const reportPreferencesSelector = (state, portfolio) => {
  if (!portfolio) {
    portfolio = currentPortfolioSelector(state);
  }
  return portfolio.reportPreference;
};

export const recapReportNodeSelector = createStateAccessSelector(
  [
    state => state,
    state => recapDataSelector(state),
    reportPreferencesSelector,
    (...args) => args[1],
    (...args) => args[2] ?? false,
    (...args) => args[3] ?? null,
    (...args) => args[4] ?? false,
    (...args) => args[5] ?? false,
    (...args) => args[6] ?? false,
    (...args) => args[7] ?? false,
    (...args) => args[8] ?? false
  ],
  (
    state,
    recapData,
    _1,
    reportId,
    returnLeafNode,
    overrideTimeRange,
    isInvestableAssetsBySheetsAndSectionsChart,
    isInvestableAssetsBySectionsChart,
    isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
    isInvestableAssetsWithoutCashBySectionsChart,
    isAssetsBySectionsChart
  ) => {
    if (!reportId === true || !recapData) {
      return null;
    }
    const reportParams = parseParams(reportId);
    if (
      reportParams.chart_option === recapChartOptions.SHEETS_AND_SECTIONS.id ||
      isInvestableAssetsBySheetsAndSectionsChart ||
      isInvestableAssetsBySectionsChart ||
      isInvestableAssetsWithoutCashBySheetsAndSectionsChart ||
      isInvestableAssetsWithoutCashBySectionsChart
    ) {
      return sheetAndSectionReportNodeSelector(
        state,
        reportId,
        overrideTimeRange,
        false,
        false,
        isInvestableAssetsBySheetsAndSectionsChart,
        isInvestableAssetsBySectionsChart,
        isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
        isInvestableAssetsWithoutCashBySectionsChart,
        isAssetsBySectionsChart
      );
    }

    const reportNodeId = reportParams.report_node_id;

    const reportPath = reportParams.report_path;
    const timeRange = overrideTimeRange || reportParams.chart_timerange;
    const reportTimeRangeNode = recapData.data[timeRange];
    const reportTotals = reportTimeRangeNode?.[reportParams.chart_option]?.totals;
    var node = null;
    if (!reportPath === true) {
      // work around to handle charts which was created before changing to stocks and funds since the older chart ids will have node id as Stocks
      if (reportParams.chart_option === recapChartOptions.STOCKS_AND_GEOGRAPHY.id && reportNodeId === "Stocks") {
        node = reportTotals?.Stocks;
      } else if (
        reportParams.chart_option === recapChartOptions.ASSET_CLASSES.id ||
        reportParams.chart_option === recapChartOptions.INVESTABLE.id ||
        reportParams.chart_option === recapChartOptions.INVESTABLE_WITHOUT_CASH.id
      ) {
        if (reportNodeId === "Non-US Funds") {
          node = reportTotals?.Funds;
        } else if (reportNodeId === "Miscellaneous") {
          node = reportTotals?.Others;
        } else {
          node = reportTotals?.[reportNodeId];
        }
      } else {
        node = reportTotals?.[reportNodeId];
      }
    } else {
      const parentNode = reportTotals[decodeURIComponent(reportPath)];
      node =
        parentNode &&
        parentNode.find(item => item.name === reportParams.report_node_id || item.id === reportParams.report_node_id);
    }
    if (returnLeafNode === false) {
      return node;
    }
    let nodeName;
    if (
      reportParams.chart_option === recapChartOptions.ASSETS_AND_CURRENCY.id &&
      (reportNodeId === "Fiat Assets" || reportNodeId === "Assets")
    ) {
      nodeName = checkIfAChartOptionHasNoDataToShow(recapChartOptions.CRYPTO.id, chartTimeRange.TODAY, recapData.data)
        ? RECAP_CATEGORY_TYPE_ASSET
        : RECAP_CATEGORY_TYPE_FIAT_ASSET;
    } else {
      nodeName =
        reportParams.chart_option === recapChartOptions.STOCKS_AND_GEOGRAPHY.id && reportNodeId === "Stocks"
          ? "Stocks"
          : reportParams.report_node_id;
    }
    return Array.isArray(node) ? node.find(item => item.name === nodeName) : node;
  }
);

export const getContentsTabTitle = (
  state,
  selectedChartOptions,
  nodeId,
  reportPath,
  reportName,
  reportId,
  shouldReturnLabel,
  isInvestableAssetsBySheetsAndSectionsChart,
  isInvestableAssetsBySectionsChart,
  isInvestableAssetsWithoutCashBySheetsAndSectionsChart,
  isInvestableAssetsWithoutCashBySectionsChart,
  isAssetsBySectionsChart
) => {
  const recapData = recapDataSelector(state);
  if (selectedChartOptions === recapChartOptions.NETWORTH.id) {
    if (nodeId === RECAP_CATEGORY_TYPE_NETWORTH) {
      return "Debt to Assets ratio";
    } else {
      return nodeId;
    }
  } else if (isInvestableAssetsBySheetsAndSectionsChart) {
    return "Investable Assets x Sheets";
  } else if (isInvestableAssetsBySectionsChart) {
    return "Investable Assets x Sections";
  } else if (isInvestableAssetsWithoutCashBySheetsAndSectionsChart) {
    return "Investable Assets ex Cash x Sheets";
  } else if (isInvestableAssetsWithoutCashBySectionsChart) {
    return "Investable Assets ex Cash x Sections";
  } else {
    if (!reportPath) {
      switch (selectedChartOptions) {
        case recapChartOptions.ASSET_CLASSES.id:
          if (nodeId === "Assets") {
            return recapChartOptions.ASSET_CLASSES.label;
          } else {
            break;
          }
        case recapChartOptions.SHEETS_AND_SECTIONS.id:
          if (nodeId === "Assets") {
            return isAssetsBySectionsChart ? "Assets x Sections" : "Assets x Sheets";
          } else if (nodeId === "Debts") {
            return "Assets x Debts";
          }
          break;
        case recapChartOptions.ASSETS_AND_CURRENCY.id:
          if (nodeId === "Fiat Assets") {
            return checkIfAChartOptionHasNoDataToShow(recapChartOptions.CRYPTO.id, chartTimeRange.TODAY, recapData.data)
              ? "Assets X Currency"
              : recapChartOptions.ASSETS_AND_CURRENCY.label;
          }
          break;
        case recapChartOptions.STOCKS_AND_SECTOR.id:
          if (nodeId === "Stocks") {
            return recapChartOptions.STOCKS_AND_SECTOR.label;
          }
          break;
        case recapChartOptions.STOCKS_AND_MARKETCAP.id:
          if (nodeId === "Stocks") {
            return recapChartOptions.STOCKS_AND_MARKETCAP.label;
          }
          break;
        case recapChartOptions.STOCKS_AND_GEOGRAPHY.id:
          if (nodeId === "Stocks & Funds" || nodeId === "Stocks") {
            return recapChartOptions.STOCKS_AND_GEOGRAPHY.label;
          }
          break;
        case recapChartOptions.CRYPTO.id:
          if (nodeId === "Crypto") {
            return "Crypto x Sector";
          }
          break;
        case recapChartOptions.TAXABLE_ASSETS.id:
          if (nodeId === "Assets") {
            return recapChartOptions.TAXABLE_ASSETS.label;
          } else {
            break;
          }
      }
    }
    if (selectedChartOptions === recapChartOptions.SHEETS_AND_SECTIONS.id) {
      if (reportPath === "Assets/sheets" || reportPath === "Debts/sheets") {
        return `${!shouldReturnLabel ? `${reportName} (Sheet)` : `${reportName}`}`;
      } else if (reportPath === "Assets/sections" || reportPath === "Debts/sections") {
        const parentNode = sheetAndSectionReportNodeSelector(state, reportId, null, true);
        return `${!shouldReturnLabel ? `${parentNode.name} / ${reportName}` : `${reportName}`}`;
      } else {
        return reportName;
      }
    }
    return nodeId;
  }
};

export const recapDataTotalInvestableAssetsValueSelector = state => {
  const investableAssetData = recapDataTotalInvestableAssetsSelector(state);
  const recapDataCurrency = recapDataCurrencySelector(state);
  const portfolioCurrency = currentPortfolioCurrencySelector(state);
  const investableValue = investableAssetData && investableAssetData.values[0] && investableAssetData.values[0].value;
  return convertCurrency(state, investableValue, recapDataCurrency, portfolioCurrency);
};

export const recapDataTotalInvestableAssetsWithoutCashValueSelector = state => {
  const investableAssetData = recapDataTotalInvestableAssetsWithoutCashSelector(state);
  const recapDataCurrency = recapDataCurrencySelector(state);
  const portfolioCurrency = currentPortfolioCurrencySelector(state);
  const investableValue = investableAssetData && investableAssetData.values[0] && investableAssetData.values[0].value;
  return convertCurrency(state, investableValue, recapDataCurrency, portfolioCurrency);
};

export const parseNumberStringToFloat = (state, input) => {
  if (input === "") {
    return null;
  }
  if (!input === true || typeof input !== "string") {
    return input;
  }

  input = cleanUpSeparatorFromNumberString(state, input).trim();

  if (getCharacterForDecimal(state) === ",") {
    input = input.replace(/,/g, ".");
  }
  if (isNaN(input)) {
    return null;
  }
  return parseFloat(input);
};

export function getExchangeRate(
  state,
  fromCurrency,
  toCurrency,
  usePreviousDayRates = false,
  date = null,
  logValues = false
) {
  if (!fromCurrency === true || !toCurrency === true) {
    return 0;
  }

  if (fromCurrency === toCurrency) {
    return 1;
  }

  const key = fromCurrency + "-" + toCurrency;
  const hardcodedRate = HARDCODED_EXCHANGE_RATES.get(key);
  if (hardcodedRate) {
    return hardcodedRate;
  }

  const previousDayRates = currencyPreviousDayExchangeRateSelector(state);
  const currentRates = currencyExchangeRateSelector(state);
  const exchangeRates = usePreviousDayRates && !previousDayRates === false ? previousDayRates : currentRates;
  var fromCurrencyExchangeRate = exchangeRates[fromCurrency];
  var toCurrencyExchangeRate = exchangeRates[toCurrency];
  let combinedCurrencyExchangeRate = 0;
  // do not get exchange rate from history for today's date
  if (
    !date === false &&
    getCustodianHistoryFormattedDateString(date) !== getCustodianHistoryFormattedDateString(toUserTimezoneDate())
  ) {
    const dateString = getDateStringForExchangeRateDate(date);
    fromCurrencyExchangeRate = exchangeRates[fromCurrency === "USD" ? fromCurrency : `${fromCurrency}-${dateString}`];
    toCurrencyExchangeRate = exchangeRates[toCurrency === "USD" ? toCurrency : `${toCurrency}-${dateString}`];
    combinedCurrencyExchangeRate = exchangeRates[`${fromCurrency}-${toCurrency}-${dateString}`];
  }

  if (logValues) {
    console.log(
      "getExchangeRate",
      getDateStringForExchangeRateDate(date),
      fromCurrency,
      fromCurrencyExchangeRate,
      toCurrency,
      toCurrencyExchangeRate,
      exchangeRates
    );
  }

  if (combinedCurrencyExchangeRate) {
    return combinedCurrencyExchangeRate;
  }
  if (!fromCurrencyExchangeRate === true || !toCurrencyExchangeRate === true) {
    return 0;
  }
  if (fromCurrency === "USD") {
    return toCurrencyExchangeRate;
  }
  if (toCurrency === "USD") {
    return 1 / fromCurrencyExchangeRate;
  }
  const exchangRateForUsd = 1 / fromCurrencyExchangeRate;
  return exchangRateForUsd * toCurrencyExchangeRate;
}

export function getTickerUsingShortName(state, shortName) {
  const supportedTickers = supportedTickerShortNameMapSelector(state);
  const result = supportedTickers[shortName];
  return result || supportedTickers[UNKNOWN_TICKER_SHORT_NAME];
}

export const getTickerSymbolToPrefix = tickerDetails => {
  if (!tickerDetails) {
    return;
  }
  if (tickerDetails && tickerDetails.type === "crypto") {
    return tickerDetails.code;
  } else {
    const tickerShortNameSuffix =
      tickerDetails &&
      tickerDetails.shortName.split(".").length > 1 &&
      tickerDetails.shortName.split(".")[1].toLowerCase();

    if (tickerShortNameSuffix === "bnd" || tickerShortNameSuffix === "euf") {
      return tickerDetails && tickerDetails.code;
    } else {
      return tickerDetails && tickerDetails.shortName;
    }
  }
};

export function getTickerUsingId(state, tickerId) {
  const supportedTickers = supportedTickerIdMapSelector(state);
  const result = supportedTickers[tickerId];
  return result || getTickerUsingShortName(state, UNKNOWN_TICKER_SHORT_NAME);
}

export const cleanUpSeparatorFromNumberString = (state, input, lookForTickers = true) => {
  if (!input === true) {
    return input;
  }

  input = input.trim();

  const numberSeparator = getCharacterForNumberSeparator(state);
  if (numberSeparator === "." && lookForTickers === true) {
    // Since tickers can have dots in between them like BTC.CC
    // don't remove dots blindly
    let finalString = "";

    for (var i = 0; i < input.length; i++) {
      const c = input[i];
      if (c === numberSeparator && (i + 1 === input.length || isNaN(input[i + 1]) === false)) {
        continue;
      }
      finalString += c;
    }

    return finalString;
  } else if (numberSeparator.charCodeAt(0) === 8239) {
    // handling for non breaking space which is used as a separator in some number formats. When the number format selected supports non breaking space
    // if a user enters value by giving space this wil be handled
    return input.split(/[\s\u00A0]+/).join("");
  } else if (numberSeparator === SWISS_RAW_SEPARATOR && input.includes(SWISS_DISPLAY_SEPARATOR)) {
    return input.split(SWISS_DISPLAY_SEPARATOR).join("");
  }
  return input.split(numberSeparator).join("");
};

export const getCharacterForDecimal = state => {
  return formatNumberWithKuberaNumberFormatSettingsSelector(state, 0.1).indexOf(",") > 0 ? "," : ".";
};

export const getDateStringForExchangeRateDate = date => {
  if (!date === true) {
    return null;
  }
  return `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}-${("0" + date.getDate()).slice(-2)}`;
};

export const getCharacterForNumberSeparator = state => {
  return formatNumberWithKuberaNumberFormatSettingsSelector(state, 10000)
    .replace(SWISS_DISPLAY_SEPARATOR, SWISS_RAW_SEPARATOR) // when typing, ' will likely be used, but if copied from excel ’ may be used
    .charAt(2);
};

const MAX_CACHED_NUMBER_FORMATTERS = 64;
const numberFormatterCache = new Map();

const getNumberFormatterCacheKey = (locale, options) => {
  if (options === undefined) {
    return `${locale === undefined ? "" : locale}\u0000`;
  }
  try {
    return `${locale === undefined ? "" : locale}\u0000${JSON.stringify(options)}`;
  } catch {
    // Options that cannot be serialised (circular, BigInt values) are still valid for
    // Intl.NumberFormat, so fall back to constructing an uncached formatter per call.
    return null;
  }
};

const getCachedNumberFormatter = (locale, options) => {
  const cacheKey = getNumberFormatterCacheKey(locale, options);
  if (cacheKey === null) {
    return new Intl.NumberFormat(locale, options);
  }
  let formatter = numberFormatterCache.get(cacheKey);
  if (formatter === undefined) {
    formatter = new Intl.NumberFormat(locale, options);
    if (numberFormatterCache.size >= MAX_CACHED_NUMBER_FORMATTERS) {
      numberFormatterCache.clear();
    }
    numberFormatterCache.set(cacheKey, formatter);
  }
  return formatter;
};

export const formatNumberWithKuberaNumberFormatSettingsSelector = (state, number, options) => {
  // if (typeof number === "string") {
  //   number = parseNumberStringToFloat(state, number);
  // }
  const kuberaNumberFormat = kuberaNumberFormatSelectorOverridenForSimilarFormatCurrencies(state);
  if (!number) {
    return number;
  }
  // Number.prototype.toLocaleString is spec-defined as Intl.NumberFormat(locale, options).format(this),
  // so the cached formatter is equivalent — but only for numbers. String and Date have their own
  // toLocaleString semantics that must not be routed through a number formatter.
  if (typeof number !== "number") {
    return number.toLocaleString(kuberaNumberFormat, options);
  }
  return getCachedNumberFormatter(kuberaNumberFormat, options).format(number);
};

export const kuberaNumberFormatSelectorOverridenForSimilarFormatCurrencies = state => {
  const portfolioCurrency = currentPortfolioCurrencySelector(state);
  const siteConfig = siteConfigSelector(state);
  const kuberaNumberFormatForROMode =
    siteConfig && siteConfig.option ? JSON.parse(siteConfig.option).kuberaNumberFormat : undefined;
  let kuberaNumberFormat;
  if (SIMILAR_FORMAT_CURRENCIES.get("INR").includes(portfolioCurrency)) {
    kuberaNumberFormat = "en-IN";
  } else {
    kuberaNumberFormat = isAppInViewMode() ? kuberaNumberFormatForROMode : kuberaNumberFormatSelector(state);
  }
  return kuberaNumberFormat;
};

export const siteConfigSelector = state => {
  return state.auth.siteConfig;
};

export const kuberaNumberFormatSelector = state => {
  const userPreferences = userPreferencesSelector(state);
  if (userPreferences && userPreferences.kuberaNumberFormat) {
    return userPreferences.kuberaNumberFormat === "systemDefault" ? undefined : userPreferences.kuberaNumberFormat;
  } else {
    return undefined;
  }
};

export const supportedTickerIdMapSelector = state => {
  return state.tickers.tickerIdMap || tickerReducerInitialState.tickerIdMap;
};

export const supportedTickerShortNameMapSelector = state => {
  return state.tickers.tickerShortNameMap || tickerReducerInitialState.tickerShortNameMap;
};

export const currencyPreviousDayExchangeRateSelector = createSelector(
  [state => state.tickers.tickerData],
  tickerData => {
    if (tickerData && !tickerData.prevExchange === false) {
      return tickerData.prevExchange.rates;
    }
    return null;
  }
);

const popularTickersSelectorByTypeDefaultValue = [];
export const popularTickersSelectorByType = (state, type) => {
  const tickerData = state.tickers.tickerData;
  return tickerData?.popularTicker?.[type] || popularTickersSelectorByTypeDefaultValue;
};

export const currencyExchangeRateSelector = createSelector(
  [state => state.tickers.tickerData],
  tickerData => tickerData?.exchange?.rates || {}
);

export const isWlClientPortfolioWriteDisabled = state => {
  const siteConfig = siteConfigSelector(state);
  return siteConfig.type === wlSetupType.ADMIN_WR_CLIENT_WR && siteConfig.allowClientPortfolioWrite === 0;
};

export const isOwnershipTagAllowedSelector = state => {
  if (isAppInViewMode() === true) {
    const capabilities = JSON.parse(siteConfigSelector(state)?.capability || "[]");
    return (
      capabilities.includes(shareCapabilities.ALL) === true ||
      capabilities.includes(shareCapabilities.OWNERSHIP) === true
    );
  }
  if (isAppInWhiteLabelMode() === false) {
    return true;
  }
  const siteConfig = siteConfigSelector(state);
  return siteConfig?.allowOwnershipTag === 1;
};
